/**
 * User profiles (feature-level RBAC).
 * Mirrors backend app/core/profiles.py.
 */

export const CONTROLLED_RESOURCES = [
  'domains',
  'usecases',
  'cadrages',
  'forges',
  'applications',
  'conversations',
  'chatbots',
  'knowledge-base',
  'evaluations',
  'configuration',
  'my-organization',
  'token-consumption',
  'genie-artifact',
  'cowork',
  'projects',
  'gallery',
  'portfolio',
] as const;

export type ControlledResource = (typeof CONTROLLED_RESOURCES)[number];

export type Intent = 'read' | 'write' | 'delete';

export interface ProfilePermission {
  resource: ControlledResource;
  read: boolean;
  write: boolean;
  delete: boolean;
}

/** Roles a profile can be marked as default for (org-scoped profiles only). */
export const DEFAULT_FOR_ROLES = ['admin', 'user', 'demo'] as const;
export type DefaultForRole = (typeof DEFAULT_FOR_ROLES)[number];

/**
 * Default welcome page a member with this profile lands on after login.
 * Mirrors backend app/core/profiles.py LandingPage. `welcome` is the
 * GenieArtifact welcome page.
 */
export const LANDING_PAGES = [
  'welcome',
  'dashboard',
  'domains',
  'conversations',
  'projects',
  'gallery',
  'portfolio',
] as const;
export type LandingPage = (typeof LANDING_PAGES)[number];

/** Read resource each landing page requires, and the route it maps to. */
export const LANDING_PAGE_META: Record<
  LandingPage,
  { resource: ControlledResource; path: string }
> = {
  welcome: { resource: 'genie-artifact', path: '/welcome' },
  dashboard: { resource: 'domains', path: '/dashboard' },
  domains: { resource: 'domains', path: '/domains' },
  conversations: { resource: 'conversations', path: '/conversations' },
  projects: { resource: 'projects', path: '/projets' },
  gallery: { resource: 'gallery', path: '/galerie' },
  portfolio: { resource: 'portfolio', path: '/portefeuille' },
};

/**
 * Per-profile UI catalogs (lot P). Mirrors backend app/core/profiles.py
 * NAV_SECTIONS and DASHBOARD_WIDGETS: ids sent in `ui_config` must be listed here.
 */
export const NAV_SECTIONS = {
  main: { welcome: 'genie-artifact', dashboard: 'domains' },
  business: { domains: 'domains', usecases: 'usecases' },
  projects: { projects: 'projects', gallery: 'gallery', portfolio: 'portfolio' },
  deployment: { cadrages: 'cadrages', cowork: 'cowork', applications: 'applications', forges: 'forges' },
  knowledge: {
    conversations: 'conversations',
    chatbots: 'chatbots',
    'knowledge-base': 'knowledge-base',
    evaluations: 'evaluations',
  },
} as const satisfies Record<string, Record<string, ControlledResource>>;

export type NavSection = keyof typeof NAV_SECTIONS;
export type NavItemId = {
  [S in NavSection]: keyof (typeof NAV_SECTIONS)[S];
}[NavSection];

export const DASHBOARD_WIDGETS = {
  kpis: 'domains',
  'timeline-velocity': 'usecases',
  domains: 'domains',
  'usecase-matrix': 'usecases',
  charts: 'usecases',
  traffic: 'conversations',
  activity: 'domains',
  contacts: 'domains',
  'cadrage-status': 'cadrages',
  'forge-apps': 'forges',
  'projects-kpis': 'portfolio',
  'my-projects': 'projects',
  'gallery-latest': 'gallery',
  'analysis-runs': 'projects',
  'portfolio-minimap': 'portfolio',
  'review-requests': 'projects',
} as const satisfies Record<string, ControlledResource>;

export type DashboardWidgetId = keyof typeof DASHBOARD_WIDGETS;

export type ProfileExperience = 'etabli' | 'olise';

/** Experience, navigation and dashboard of a profile; null fields inherit the default. */
export interface ProfileUiConfig {
  experience?: ProfileExperience | null;
  navigation?: {
    hidden?: NavItemId[];
    order?: Partial<Record<NavSection, NavItemId[]>>;
  } | null;
  dashboard?: { widgets: { id: DashboardWidgetId; visible?: boolean }[] } | null;
}

export interface Profile {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  is_system: boolean;
  organization_id: string | null;
  default_for_role: DefaultForRole | null;
  landing_page: LandingPage | null;
  ui_config?: ProfileUiConfig | null;
  permissions: ProfilePermission[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ProfileCreatePayload {
  key: string;
  name: string;
  description?: string | null;
  permissions: ProfilePermission[];
  /** Super-admin only; org admins are forced to their own org server-side. */
  organization_id?: string | null;
  /** Only valid for org-scoped profiles. */
  default_for_role?: DefaultForRole | null;
  /** Default welcome page for members with this profile (null → server default). */
  landing_page?: LandingPage | null;
  ui_config?: ProfileUiConfig | null;
}

export interface ProfileUpdatePayload {
  name?: string;
  description?: string | null;
  permissions?: ProfilePermission[];
  /** Explicit null clears it. Only valid for org-scoped profiles. */
  default_for_role?: DefaultForRole | null;
  /** Default welcome page for members with this profile (null → server default). */
  landing_page?: LandingPage | null;
  /** Explicit null resets to the default UI. */
  ui_config?: ProfileUiConfig | null;
}

export interface ProfileDuplicatePayload {
  name: string;
  key?: string;
  /** Super-admin only (null = global); org admins always duplicate into their org. */
  organization_id?: string | null;
}

/** Portable JSON of a profile (GET /profiles/{id}/export, POST /profiles/import). */
export interface ProfileExport {
  format: 'etabli.profile';
  version: 1;
  key: string;
  name: string;
  description?: string | null;
  permissions: ProfilePermission[];
  landing_page?: LandingPage | null;
  ui_config?: ProfileUiConfig | null;
}

export interface ProfileImportPayload extends ProfileExport {
  /** Super-admin only (null = global); org admins always import into their org. */
  organization_id?: string | null;
}

export interface ProfileUser {
  id: string;
  email: string;
  full_name?: string | null;
  role: string;
  organization_id?: string | null;
  is_active: boolean;
}

export const SYSTEM_PROFILE_KEYS = [
  'god',
  'admin',
  'metier',
  'developer',
  'olise-member',
  'olise-manager',
  'simple-member',
  'simple-manager',
] as const;
export type SystemProfileKey = (typeof SYSTEM_PROFILE_KEYS)[number];
